import { supabase } from '../lib/supabase.js';
import { createClient } from '@supabase/supabase-js';

/**
 * Vytvoří izolovaný Supabase klient (bez sdílené session) pro admin operaci,
 * aby signUp() nevyhodil aktuálního admina.
 */
function makeTempClient() {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    return createClient(url, key, {
        auth: {
            persistSession: false,
            storage: { getItem: () => null, setItem: () => {}, removeItem: () => {} }
        }
    });
}

async function addMusician({ email, password, name, musician_role, share_percent }) {
    const temp = makeTempClient();
    const { data, error } = await temp.auth.signUp({ email, password });
    if (error) return { error };
    const newUserId = data?.user?.id;
    if (!newUserId) return { error: { message: 'Nepodařilo se získat ID nového uživatele.' } };
    // Počkej až trigger handle_new_user vytvoří profil (max ~3 s, kontrola každých 300 ms).
    let profileReady = false;
    for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 300));
        const { data: p } = await supabase.from('profiles').select('id').eq('id', newUserId).maybeSingle();
        if (p) { profileReady = true; break; }
    }
    if (!profileReady) return { error: { message: 'Profil muzikanta nebyl vytvořen (trigger selhal). Zkus to znovu.' } };
    const { error: e2 } = await supabase.from('profiles').update({
        name, musician_role, share_percent: parseInt(share_percent, 10) || 100, is_default: true
    }).eq('id', newUserId);
    if (e2) return { error: e2 };
    return { userId: newUserId };
}

function esc(s) { if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

async function loadSettings() {
    const { data } = await supabase.from('settings').select('key, value');
    return Object.fromEntries((data || []).map(s => [s.key, s.value]));
}

function flashStatus(msg, type = 'ok') {
    const bar = document.getElementById('save-status');
    if (!bar) return;
    bar.textContent = msg;
    bar.dataset.type = type;
    bar.style.opacity = '1';
    clearTimeout(flashStatus._t);
    flashStatus._t = setTimeout(() => { bar.style.opacity = '0.5'; bar.textContent = 'Změny se ukládají automaticky'; bar.dataset.type = 'idle'; }, 2500);
}

async function saveSetting(key, value, inputEl) {
    const { error } = await supabase.from('settings').upsert({ key, value });
    if (error) {
        flashStatus('Chyba: ' + error.message, 'err');
        if (inputEl) inputEl.classList.add('save-error');
        console.error(error);
    } else {
        flashStatus(`Uloženo ✓ ${key} = ${value}`, 'ok');
        if (inputEl) {
            inputEl.classList.add('save-ok');
            setTimeout(() => inputEl.classList.remove('save-ok'), 800);
        }
        // Název kapely se propíše do hlavičky hned, bez nutnosti reloadu.
        if (key === 'band_name') {
            const h1 = document.querySelector('header .brand h1');
            if (h1) h1.textContent = String(value).trim() || 'Kapela';
        }
        // Vyčistit cache komprese, aby další upload použil nové hodnoty
        const { clearCompressCache } = await import('../lib/photos.js');
        clearCompressCache();
    }
}

async function updateProfile(id, fields, inputEl) {
    const { error } = await supabase.from('profiles').update(fields).eq('id', id);
    if (error) {
        flashStatus('Chyba: ' + error.message, 'err');
        console.error(error);
    } else {
        flashStatus(`Uloženo ✓ profil`, 'ok');
        if (inputEl) {
            inputEl.classList.add('save-ok');
            setTimeout(() => inputEl.classList.remove('save-ok'), 800);
        }
    }
}

async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) alert('Chyba: ' + error.message);
    else alert(`E-mail s odkazem pro reset hesla odeslán na ${email}.`);
}

function settingRow(label, key, settings, type = 'number', unit = '') {
    const val = settings[key] ?? '';
    return `
        <div class="settings-row">
            <label>${esc(label)}${unit ? ` <span class="unit">${unit}</span>` : ''}</label>
            <input type="${type}" data-setting="${esc(key)}" value="${esc(val)}">
        </div>
    `;
}

function highlightSettingRow(label, key, settings, unit = '') {
    const val = settings[key] ?? '';
    return `
        <div class="settings-row highlight">
            <label>${esc(label)}${unit ? ` <span class="unit">${unit}</span>` : ''}</label>
            <input type="number" data-setting="${esc(key)}" value="${esc(val)}">
        </div>
    `;
}

function musicianRow(p, isCurrentUser) {
    const isHost = p.musician_role === 'stalý host';
    const isAdmin = p.role === 'admin';
    return `
        <tr class="${isHost ? 'is-host' : ''}${isAdmin ? ' is-admin' : ''}" data-id="${p.id}">
            <td class="name-cell"><input type="text" data-field="name" value="${esc(p.name || '')}"></td>
            <td>
                <select data-field="musician_role">
                    <option value="stalý člen"${p.musician_role === 'stalý člen' ? ' selected' : ''}>Stálý člen</option>
                    <option value="stalý host"${p.musician_role === 'stalý host' ? ' selected' : ''}>Stálý host</option>
                    <option value="jednorázový"${p.musician_role === 'jednorázový' ? ' selected' : ''}>Jednorázový</option>
                </select>
            </td>
            <td class="share-cell">
                <input type="number" data-field="share_percent" value="${p.share_percent ?? 100}">
                <span class="suffix">%</span>
            </td>
            <td>
                <select data-field="home_city">
                    <option value="Plzeň"${p.home_city === 'Plzeň' ? ' selected' : ''}>Plzeň</option>
                    <option value="Stříbro"${p.home_city === 'Stříbro' ? ' selected' : ''}>Stříbro</option>
                </select>
            </td>
            <td class="email-cell"><input type="email" value="${esc(p.email || '')}" readonly style="opacity:0.7;cursor:default;"></td>
            <td>
                ${isAdmin
                    ? '<span class="account-badge admin">Administrátor</span>'
                    : '<span class="account-badge active">Aktivní</span>'}
                <div class="account-actions">
                    <button class="mini-btn" data-action="edit"><strong>Upravit</strong></button>
                    ${p.email ? `<button class="mini-btn" data-action="reset" data-email="${esc(p.email)}">Reset hesla</button>` : ''}
                    ${!isAdmin && !isCurrentUser ? `<button class="mini-btn" data-action="make-admin">+ admin</button>` : ''}
                    ${isAdmin && !isCurrentUser ? `<button class="mini-btn" data-action="demote">− admin</button>` : ''}
                    ${!isCurrentUser ? `<button class="mini-btn" data-action="remove">Smazat</button>` : ''}
                </div>
            </td>
            <td><label class="default-cb"><input type="checkbox" data-field="is_default"${p.is_default ? ' checked' : ''}></label></td>
            <td><label class="default-cb" title="Předvyplnit osobní dopravu na nové akci"><input type="checkbox" data-field="default_own_transport"${p.default_own_transport ? ' checked' : ''}></label></td>
        </tr>
    `;
}

function openEditMusicianModal(profile, onSaved) {
    let m = document.getElementById('edit-muz-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'edit-muz-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window" style="max-width:480px;">
            <div class="modal-head">
                <h2>Upravit muzikanta: ${esc(profile.name)}</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="em-form">
                <div class="modal-body">
                    <div class="spec-section">
                        <div class="spec-row"><label>Jméno</label><input type="text" name="name" value="${esc(profile.name || '')}" required style="min-width:220px; text-align:left;"></div>
                        <div class="spec-row"><label>E-mail (login)</label><input type="email" value="${esc(profile.email || '')}" readonly style="min-width:220px; text-align:left; opacity:0.7;"></div>
                        <div class="spec-row">
                            <label>Role muzikanta</label>
                            <select name="musician_role">
                                <option value="stalý člen"${profile.musician_role === 'stalý člen' ? ' selected' : ''}>Stálý člen</option>
                                <option value="stalý host"${profile.musician_role === 'stalý host' ? ' selected' : ''}>Stálý host</option>
                                <option value="jednorázový"${profile.musician_role === 'jednorázový' ? ' selected' : ''}>Jednorázový</option>
                            </select>
                        </div>
                        <div class="spec-row"><label>Podíl mzdy <span class="unit">%</span></label><input type="number" name="share_percent" value="${profile.share_percent ?? 100}" min="0" max="200"></div>
                        <div class="spec-row">
                            <label>Bydlí (z kterého města jezdí)</label>
                            <select name="home_city">
                                <option value="Plzeň"${profile.home_city === 'Plzeň' ? ' selected' : ''}>Plzeň</option>
                                <option value="Stříbro"${profile.home_city === 'Stříbro' ? ' selected' : ''}>Stříbro</option>
                            </select>
                        </div>
                        <div class="spec-row">
                            <label>Systémová role</label>
                            <select name="role">
                                <option value="user"${profile.role === 'user' ? ' selected' : ''}>Muzikant (user)</option>
                                <option value="admin"${profile.role === 'admin' ? ' selected' : ''}>Administrátor</option>
                            </select>
                        </div>
                        <div class="spec-row">
                            <label>Defaultně v nové akci</label>
                            <label class="check-single"><input type="checkbox" name="is_default"${profile.is_default ? ' checked' : ''}> Předvyplnit</label>
                        </div>
                        <div class="spec-row">
                            <label>Vlastní osobní doprava</label>
                            <label class="check-single"><input type="checkbox" name="default_own_transport"${profile.default_own_transport ? ' checked' : ''}> Předvyplnit „osobní doprava" na akci</label>
                        </div>
                    </div>
                    <p style="font-size:12px;color:var(--text-muted);margin-top:10px;">
                        Změna e-mailu vyžaduje Supabase Auth. Pokud chceš změnit, dej muzikantovi <em>Reset hesla</em>
                        a nechej ho nastavit nový profil.
                    </p>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="em-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">Uložit změny</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);
    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#em-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });

    m.querySelector('#em-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        const update = {
            name: fd.get('name').trim(),
            musician_role: fd.get('musician_role'),
            share_percent: parseInt(fd.get('share_percent'), 10) || 100,
            home_city: fd.get('home_city') || 'Plzeň',
            role: fd.get('role'),
            is_default: fd.get('is_default') === 'on',
            default_own_transport: fd.get('default_own_transport') === 'on'
        };
        const { error } = await supabase.from('profiles').update(update).eq('id', profile.id);
        if (error) { alert('Chyba: ' + error.message); return; }
        close();
        onSaved?.();
    });
}

async function deleteMusician(profile) {
    if (!confirm(`Opravdu smazat muzikanta "${profile.name}"?\n\nProfil se smaže z databáze. Účet v Supabase Auth zůstane (pro úplné odstranění jdi do Authentication → Users).`)) return;
    const { error } = await supabase.from('profiles').delete().eq('id', profile.id);
    if (error) {
        if (error.message.includes('foreign key') || error.message.includes('violates')) {
            alert('Nelze smazat: muzikant je vázaný na akce nebo výplaty.\n\nNejdřív odeber z event_musicians / payouts, nebo nastav místo smazání is_default=false (přestane se předvyplňovat na nové akce).');
        } else {
            alert('Chyba: ' + error.message);
        }
        return false;
    }
    return true;
}

export async function renderNastaveni(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const [settings, { data: profiles, error }, { data: { user: currentUser } }] = await Promise.all([
        loadSettings(),
        supabase.from('profiles').select('*').order('created_at'),
        supabase.auth.getUser()
    ]);

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(error.message)}</div>`;
        return;
    }

    container.innerHTML = `
        <div class="filters" style="align-items:center;">
            <h2 style="font-size:18px; margin-right:auto;">Nastavení aplikace</h2>
            <span id="save-status" data-type="idle" style="font-size:13px; padding:6px 12px; border-radius:999px; transition:opacity 0.3s; opacity:0.5;">Změny se ukládají automaticky</span>
        </div>

        <div class="settings-grid">
            <div class="settings-card wide">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
                    <h3>Databáze muzikantů</h3>
                    <button class="btn btn-sm btn-primary" id="add-musician-btn">+ Přidat muzikanta</button>
                </div>
                <p class="hint">
                    <strong>Podíl mzdy</strong> – stálý člen 100 %, host obvykle 70 %.
                    <strong>E-mail</strong> slouží pro přihlášení (Supabase Auth).
                    <strong>Default</strong> – automaticky se předvyplní u nové akce.
                    Reset hesla pošle e-mail s odkazem.
                </p>
                <table class="settings-musicians">
                    <thead>
                        <tr>
                            <th>Jméno</th>
                            <th>Role</th>
                            <th class="num">Podíl</th>
                            <th>Bydlí</th>
                            <th>E-mail (login)</th>
                            <th>Účet &amp; akce</th>
                            <th style="text-align:center;">Default</th>
                            <th style="text-align:center;" title="Vlastní osobní doprava — předvyplní se na nové akci">Doprava</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${(profiles || []).map(p => musicianRow(p, p.id === currentUser?.id)).join('')}
                    </tbody>
                </table>
                <p class="hint" style="margin-top:14px;">
                    Tlačítkem <strong>+ Přidat muzikanta</strong> vytvoříš nový účet přímo z aplikace
                    (e-mail + dočasné heslo). Muzikant si pak může heslo změnit přes „Reset hesla".
                </p>
            </div>

            <div class="settings-card">
                <h3>Výpočet mezd</h3>
                <p class="hint">Cílová kapelnická mzda určuje, kolik chceš pro sebe nechat „nahoře" po vyplacení muzikantů.</p>
                ${highlightSettingRow('Očekávaná kapelnická mzda', 'target_band_wage', settings, 'Kč / akce')}
                ${settingRow('Zaokrouhlení mzdy', 'rounding_step', settings, 'number', 'Kč (dolů)')}
            </div>

            <div class="settings-card">
                <h3>Sazby dopravy</h3>
                <p class="hint">Snapshot k akci – pozdější změna ovlivní jen budoucí akce.</p>
                ${settingRow('Dodávka', 'rate_van_per_km', settings, 'number', 'Kč / km')}
                ${settingRow('Osobní automobil', 'rate_car_per_km', settings, 'number', 'Kč / km')}
            </div>

            <div class="settings-card">
                <h3>Plánovač času</h3>
                <p class="hint">Předstih = 75 + km (z těchto hodnot).</p>
                ${settingRow('Rezerva před začátkem', 'time_reserve_start', settings, 'number', 'min')}
                ${settingRow('Stavba jeviště', 'time_stage_setup', settings, 'number', 'min')}
                ${settingRow('Stěhování aparatury', 'time_moving', settings, 'number', 'min')}
                ${settingRow('Doprava', 'time_per_km', settings, 'number', 'min / km')}
                ${settingRow('Rezerva odjezdu', 'time_reserve_departure', settings, 'number', 'min')}
            </div>

            <div class="settings-card">
                <h3>Repertoár</h3>
                <p class="hint">Přechod mezi písněmi (potlesk, ladění, mluva). Připočítává se ke každé písni v součtu bloku.</p>
                ${settingRow('Přechod mezi písněmi', 'song_overhead_sec', settings, 'number', 's / píseň')}
            </div>

            <div class="settings-card">
                <h3>Název kapely</h3>
                <p class="hint">Zobrazuje se v horní hlavičce aplikace a v patičce PDF playlistu před názvem playlistu.</p>
                ${settingRow('Název kapely', 'band_name', settings, 'text', '')}
            </div>

            <div class="settings-card">
                <h3>Komprese fotek při uploadu</h3>
                <p class="hint">Automatické zmenšení nahrávaných fotek (úspora místa + rychlejší načítání). Hodnoty platí pro další upload – už nahrané fotky zůstávají v původní kvalitě.</p>
                <div class="settings-row">
                    <label>Foto z akce · max šířka <span class="unit">px</span></label>
                    <input type="number" data-setting="photo_event_max_width" value="${settings.photo_event_max_width ?? 2560}">
                </div>
                <div class="settings-row">
                    <label>Foto z akce · kvalita <span class="unit">% (0–100)</span></label>
                    <input type="number" data-setting="photo_event_quality" min="10" max="100" value="${settings.photo_event_quality ?? 92}">
                </div>
                <div class="settings-row">
                    <label>Foto technika · max šířka <span class="unit">px</span></label>
                    <input type="number" data-setting="photo_tech_max_width" value="${settings.photo_tech_max_width ?? 1920}">
                </div>
                <div class="settings-row">
                    <label>Foto technika · kvalita <span class="unit">% (0–100)</span></label>
                    <input type="number" data-setting="photo_tech_quality" min="10" max="100" value="${settings.photo_tech_quality ?? 85}">
                </div>
            </div>

            <div class="settings-card">
                <h3>Výchozí rozpočet nové akce</h3>
                <p class="hint">Předvyplní se u nové akce – lze ručně přepsat.</p>
                ${settingRow('Pronájem aparatury', 'default_equipment_rent', settings, 'number', 'Kč')}
                ${settingRow('Stravné', 'default_food_allowance', settings, 'number', 'Kč')}
            </div>

            <div class="settings-card">
                <h3>Role a oprávnění</h3>
                <p class="hint">Aplikace má dvě úrovně přístupu (Supabase Auth).</p>
                <ul class="auth-info-list">
                    <li>
                        <span class="role-name">Administrátor <span class="role-tag kapelnik">admin</span></span>
                        <span class="role-perms">finance, výplaty, nastavení, fond</span>
                    </li>
                    <li>
                        <span class="role-name">Muzikant <span class="role-tag muzikant">user</span></span>
                        <span class="role-perms">přehled, historie, galerie, místa, vlastní výplaty</span>
                    </li>
                </ul>
            </div>
        </div>

        <div class="footer-note">
            Změny se ukládají do Supabase automaticky po opuštění buňky (onchange).
            Snapshot sazeb se uchovává u každé akce – historie zůstává neměnná.
        </div>
    `;

    // + Přidat muzikanta
    const addBtn = container.querySelector('#add-musician-btn');
    if (addBtn) addBtn.addEventListener('click', () => openAddMusicianModal(container));

    // Settings inputs save (na change = po opuštění buňky)
    container.querySelectorAll('[data-setting]').forEach(input => {
        input.addEventListener('change', () => {
            // Číselná pole ukládej jako číslo, textová (název kapely) jako text.
            const value = input.type === 'number'
                ? (parseFloat(input.value) || 0)
                : input.value.trim();
            saveSetting(input.dataset.setting, value, input);
        });
    });

    // Profile inputs save
    container.querySelectorAll('tr[data-id]').forEach(row => {
        const id = row.dataset.id;
        row.querySelectorAll('[data-field]').forEach(input => {
            input.addEventListener('change', () => {
                const field = input.dataset.field;
                let value;
                if (field === 'is_default' || field === 'default_own_transport') value = input.checked;
                else if (field === 'share_percent') value = parseInt(input.value, 10) || 100;
                else value = input.value;
                updateProfile(id, { [field]: value }, input);
            });
        });

        // (handlers pro akce muzikanta dál)
        row.querySelectorAll('[data-action]').forEach(btn => {
            btn.addEventListener('click', async () => {
                const action = btn.dataset.action;
                const profile = profiles.find(p => p.id === id);
                if (!profile) return;

                if (action === 'edit') {
                    openEditMusicianModal(profile, () => renderNastaveni(container));
                } else if (action === 'remove') {
                    const ok = await deleteMusician(profile);
                    if (ok) renderNastaveni(container);
                } else if (action === 'reset') {
                    if (confirm(`Odeslat e-mail pro reset hesla na ${btn.dataset.email}?`)) {
                        await resetPassword(btn.dataset.email);
                    }
                } else if (action === 'make-admin') {
                    if (confirm('Povýšit tohoto muzikanta na administrátora?')) {
                        await updateProfile(id, { role: 'admin' });
                        renderNastaveni(container);
                    }
                } else if (action === 'demote') {
                    if (confirm('Odebrat administrátorské oprávnění?')) {
                        await updateProfile(id, { role: 'user' });
                        renderNastaveni(container);
                    }
                }
            });
        });
    });
}

function openAddMusicianModal(container) {
    let m = document.getElementById('add-muz-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'add-muz-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window" style="max-width:480px;">
            <div class="modal-head">
                <h2>Přidat muzikanta</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="add-muz-form">
                <div class="modal-body">
                    <div class="hint">Vytvoří nový účet v Supabase Auth + záznam v <code>profiles</code>. Muzikant pak může změnit heslo přes „Reset hesla".</div>
                    <div class="spec-section">
                        <div class="spec-row"><label>Jméno</label><input type="text" name="name" required style="min-width:220px;text-align:left;"></div>
                        <div class="spec-row"><label>E-mail (login)</label><input type="email" name="email" required style="min-width:220px;text-align:left;"></div>
                        <div class="spec-row"><label>Dočasné heslo <span class="unit">min. 6 znaků</span></label><input type="text" name="password" required minlength="6" value="Kapela2026" style="min-width:220px;text-align:left;"></div>
                        <div class="spec-row">
                            <label>Role muzikanta</label>
                            <select name="musician_role">
                                <option value="stalý člen">Stálý člen</option>
                                <option value="stalý host">Stálý host</option>
                                <option value="jednorázový">Jednorázový</option>
                            </select>
                        </div>
                        <div class="spec-row"><label>Podíl mzdy <span class="unit">%</span></label><input type="number" name="share_percent" value="100"></div>
                    </div>
                    <p id="add-muz-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="add-muz-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit" id="add-muz-submit">Vytvořit účet</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#add-muz-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });

    const form = m.querySelector('#add-muz-form');
    const err = m.querySelector('#add-muz-error');
    const submitBtn = m.querySelector('#add-muz-submit');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        err.style.display = 'none';
        submitBtn.disabled = true; submitBtn.textContent = 'Vytvářím…';
        const fd = new FormData(form);
        const { userId, error } = await addMusician({
            email: fd.get('email').trim(),
            password: fd.get('password'),
            name: fd.get('name').trim(),
            musician_role: fd.get('musician_role'),
            share_percent: fd.get('share_percent')
        });
        if (error) {
            err.textContent = 'Chyba: ' + (error.message || error);
            err.style.display = 'block';
            submitBtn.disabled = false; submitBtn.textContent = 'Vytvořit účet';
            return;
        }
        close();
        renderNastaveni(container);
    });
}
